import { createAsyncThunk } from "@reduxjs/toolkit";
import axiosInstance, { axiosCacheInstance } from "../../../axiosSetup";
import { RootState } from "../template/store";
import { toast } from "react-toastify";

export const resetPassword = createAsyncThunk(
  'mappingDashboard/resetPassword',
  async (payload: any) => {
    try {
      const email = typeof payload === 'string'
        ? payload
        : (payload?.email ?? payload?.EmailId ?? payload?.Email ?? payload);
      if (!email) throw new Error('Email is required');

      const response = await axiosInstance.post(
        `/api/user-management/v1/users/password/forgot`,
        { email }
      );

      if (!response || !response.data) {
        console.error('Invalid response');
      }
      if (response.data.responseCode === 'INVALID') {
        toast.error(response.data.responseMsg);
      } else {
        toast.success(response.data.responseMsg);
      }
      return response.data;
    } catch (error) {
      console.error('Error: resetPassword in MappingActions.ts', error);
      toast.error('Password Reset Failed!');
      throw error;
    }
  }
);

export const unlockUser = createAsyncThunk(
  'mappingDashboard/unlockUser',
    async (payload: { mstUsrId: string }) => {
    try {
      const response = await axiosInstance.post(
                `/api/user-management/v1/users/${payload.mstUsrId}/unlock`
      );
            toast.success('User unlocked successfully.');
            return response.data;
    } catch (error) {
            const responseMsg = (error as any)?.response?.data?.responseMsg;
            toast.error(responseMsg || 'Unlock User Failed!');
      throw error;
    }
  }
);

const resendUserEmail = async (userId: string) => {
    try {
        const response = await axiosInstance.post(
            `/api/user-management/v1/users/email/admin/resend/${userId}`
        );
        const responseMsg = response.data?.responseMsg;
        if (response.data?.responseCode === 'SUCCESS') {
            toast.success(responseMsg);
        } else {
            toast.error(responseMsg || 'Unable to resend email.');
        }
        return response.data;
    } catch (error) {
        const responseMsg = (error as any)?.response?.data?.responseMsg;
        const responseCode = (error as any)?.response?.data?.responseCode;
        // Show "Email already verified" as success toast instead of error
        if (responseCode === 'OPERATION_NOT_ALLOWED') {
            toast.success(responseMsg);
        } else {
            toast.error(responseMsg || 'Unable to resend email.');
        }
        throw error;
    }
};

const resendPasswordSetupEmail = async (userId: string) => {
    try {
        const response = await axiosInstance.post(
            `/api/user-management/v1/users/password/admin/resend/${userId}`
        );
        const responseMsg = response.data?.responseMsg;
        if (response.data?.responseCode === 'SUCCESS') {
            toast.success(responseMsg);
        } else {
            toast.error(responseMsg || 'Unable to resend email.');
        }
        return response.data;
    } catch (error) {
        const responseMsg = (error as any)?.response?.data?.responseMsg;
        const responseCode = (error as any)?.response?.data?.responseCode;
        // Show "Email already verified" as success toast instead of error
        if (responseCode === 'OPERATION_NOT_ALLOWED') {
            toast.success(responseMsg);
        } else {
            toast.error(responseMsg || 'Unable to resend email.');
        }
        throw error;
    }
};

export const resendEmailVerification = createAsyncThunk(
    'mappingDashboard/resendEmailVerification',
    async (userId: string) => resendUserEmail(userId)
);

export const resendPasswordSetup = createAsyncThunk(
    'mappingDashboard/resendPasswordSetup',
    async (userId: string) => resendPasswordSetupEmail(userId)
);

export const getCompanyDetails = createAsyncThunk(
    'mappingDashboard/getCompanyDetails',
    async (payload:{custId:any})=>{
        try{
            const data =  (await axiosInstance.get(
                `/api/user-management/v1/customer/getCompanyDetailsById?mstPartnerId=${payload.custId}`
            )).data[0]
            return {
                companyName: data.customerName??'',
                phoneNo:data.phnNo??'',
                taxId:data.taxId??'',
                address:data.address??'',
                country:data.country??'',
                partnerType:data.partnerType??''
            }
        }catch{
            return {
                companyName: '',
                phoneNo:'',
                taxId:'',
                address:'',
                country:'',
                partnerType:''
            }
        }

})


export const getUserDataList = createAsyncThunk(
    'mappingDashboard/getUserDataList',
    async (payload:{custId:any})=>{
        try{
            const response = await axiosInstance.get(
                `/api/user-management/v1/customer/getUserByMstPartnerId?mstPartnerId=${payload.custId}`
            )

            // merging duplicate Ids into a single entry
            const data: any[] = []
            response.data.forEach((i:any) => {
                const row = data.find(x=>x.id == i.mstUsrId)
                if(!row)data.push({
                    id: i.mstUsrId,
                    email: i.email,
                    name: i.userName,
                    group: [i.mstGrpId],
                    pwdExpired: i.pwdExpired === 'Y' ? 'Y' : 'N',
                    // preserve numeric status from backend (AccountStatus id)
                    status: Number(i.status) ?? 0,
                    grpType: [i.grpType],
                    isRootUser: i.isRootUser,
                    title: i.title,
                    userGrpName: [i.userGrpName],
                    mstpartnerId: i.mstpartnerId,
                    phnNo: i.phnNo,
                    country: i.country,
                    loginAttempt: i.loginAttempt ?? 0,
                    countryAccess: i.countryAccess ?? '',
                    locationAccess: Array.isArray(i.locationAccess)
                      ? i.locationAccess.join(',')
                      : (i.locationAccess ?? ''),
                    appName: Array.isArray(i.appName) ? i.appName : []
                })
                else{
                    row?.group?.push(i.mstGrpId)
                    row?.userGrpName?.push(i.userGrpName)
                    row?.grpType?.push(i.grpType)
                    // Preserve existing pwdExpired if already set for this merged row
                    if (!row?.pwdExpired || row.pwdExpired === '') {
                      row.pwdExpired = i.pwdExpired === 'Y' ? 'Y' : 'N'
                    }
                }
            });
            return data
        }catch{
            return []
        }
})

// export const getCustomerCodeDataList = createAsyncThunk(
//     'mappingDashboard/getCustomerCodeDataList',
//     async (payload:{custId:any})=>{
//         try{
//             const data =  (await axiosInstance.get(
//                 `/api/user-management/v1/customer/getCustomerCodeByPartnerId?mstPartnerId=${payload.custId}`
//             )).data
//             return data.map((i:any)=>({
//                 id: i?.customerMapId,
//                 liner: i?.linerId,
//                 customerCode: i?.mapCode,
//                 matchCode: i?.internalCode,
//             }))
//         }catch{
//             return []
//         }
//     })

    export const getCustomerCodeDataList = createAsyncThunk(
    'mappingDashboard/getCustomerCodeDataList',
    async (payload:{custId:any,linerId:any})=>{
        try{
            const data =  (await axiosInstance.get(
                `/api/user-management/v1/customer/getCustomerCodeByPartnerIdAndLinerId?mstPartnerId=${payload.custId}&linerId=${payload.linerId}`
            )).data
            return data.map((i:any)=>({
                id: i?.customerMapId,
                liner: i?.linerId,
                customerCode: i?.mapCode,
                matchCode: i?.internalCode,
            }))
        }catch{
            return []
        }
    })


export const getCodeSearchListData = createAsyncThunk(
    'mappingDashboard/getCodeSearchListData',
    async (payload:{liner:any,code:any})=>{

        try{
            const data = (await axiosInstance.get(
                `/api/user-management/v1/customer/findCustomerMapping?linerId=${payload.liner}&code=${payload.code}`
            )).data
            
            return data.map((i:any)=>({
                id: i.customerMapId,
                liner:i.linerId,
                customerCode: i.customerCode,
                matchCode: i.matchCode,
                name: i.customerName,
                address: i.address,
                taxId: i.taxId,
                country: i.country,
                mstPartnerId: i.mstPartnerId,
                selected: false
            }))

        }catch{
            return []
        }
    }
)

export const getMatchCodeListData = createAsyncThunk(
    'mappingDashboard/getMatchCodeListData',
    async (payload:any,{getState}:any)=>{
        const state:RootState = getState()
        try{
            const data = (await axiosInstance.post(
                `/api/user-management/v1/customer/getUserByMatchCode?mstPartnerId=${payload.custId}`,
                {
                    matchCode: state.mappingDashboard.codeSearchListData?.filter(i=>i.selected == true).map(i=>i.matchCode)      
                }
            )).data
            return data.map((d:any)=>({
                 mstUsrId: d.mstUsrId,
                matchCode: d.matchCode,
                customerCode: d.customerCode,
                email: d.email,
                name: d.partnerNm,
                mstPartnerId: d.mstpartnerId,
                selected: false
            }))
        }catch{
            return []
        }
    }
)

export const getGroupTypes = createAsyncThunk('mappingDashboard/getGroupTypes',
    async (country:any,) => {
        if(!country || country == '')return []
        //  debugger
        try {
          const response = await axiosCacheInstance({
            url: `/api/user-management/v1/users/getGroupList?countryCode=${country}`,
            method: 'POST',
            cache:{
                ttl: 10 * 1000 * 60
            }
          });
          return response.data.map((i:any)=>({value:i.mstGrpId,label:i.usrGrpNm}));
        } catch (e) {    
          return [];
        }
      
})

export const getGroupbyTypes = createAsyncThunk(
  'mappingDashboard/getGroupListByType',
  async ({
    country,
    type,
  }: {
    country: string;
    type: string;
  }) => {
    if (!country || !type) {
      return [];
    }

    try {
      const response = await axiosCacheInstance({
        url: `/api/user-management/v1/users/getGroupListByType?countryCode=${country}&type=${type}`,
        method: 'POST',
        cache: {
          ttl: 10 * 1000 * 60,
        },
      });

      return response.data.map((i: any) => ({
        value: i.mstGrpId,
        label: i.usrGrpNm,
      }));
    } catch (e) {
      return [];
    }
  }
);

export const getAllApps = createAsyncThunk(
  'mappingDashboard/getAllApps',
  async () => {
    try {
      const response = await axiosInstance.get(
        `/api/user-management/v1/app/allApps`
      );
      return (response.data || []).map((i: any) => ({
        value: i.name,
        label: i.name,
      }));
    } catch (e) {
      return [];
    }
  }
);




export const mergeMatchCodesToUser = createAsyncThunk('mappingDashboard/mergeMatchCodesToUser',
    async (payload:any, {getState}:any)=>{
        const state:RootState = getState()
        try{
            const response = await axiosInstance.post(
                `/api/user-management/v1/customer/getMatchCodeToMerge?mstPartnerId=${payload.custId}`,
                {
                    matchCode: state.mappingDashboard.codeSearchListData?.filter(i=>i.selected == true).map(i=>i.matchCode)      
                }
            )
            toast.success("Codes mergerd Successfully!")
        }catch{
            console.error('Error: mergeMatchCodesToUser in MappingActions.ts')
            toast.error("Codes merge Failed!")
        }

    }
)


export const mergeMatchCodesToUserNew = createAsyncThunk('mappingDashboard/mergeMatchCodesToUserNew',
    async (payload:any, {getState}:any)=>{
        const state:RootState = getState()
        const selectedRows = state.mappingDashboard.codeSearchListData?.filter(i=>i.selected == true)
        const selectedUsersRows = state.mappingDashboard.matchCodeList?.filter(i=>i.selected == true)

        const newPayload = selectedRows?.map(row => (
            {
            customerMapId: row.id,
            linerId: row.liner,
            customerCode: row.customerCode,
            matchCode: row.matchCode,
            mstPartnerId: row.mstPartnerId,
            users: selectedUsersRows?.filter(element => {
                return (row.mstPartnerId == element.mstPartnerId && row.matchCode == element.matchCode)
            })?.map(e => ({mstUsrId: e.mstUsrId}))
        }));
        try{
            const response = await axiosInstance.post(
                `/api/user-management/v1/customer/mergeMatchCode?targetMstPartnerId=${payload.custId}`,
                newPayload
            )
            toast.success("Codes mergerd Successfully!")
        }catch{
            console.error('Error: mergeMatchCodesToUser in MappingActions.ts')
            toast.error("Codes merge Failed!")
        }

    }
)


export const saveUserDetails = createAsyncThunk('mappingDashboard/saveEditUserDetails',
    async (payload:any, {getState}:any)=>{
        const state:RootState = getState()
        try{
            const response:any = await axiosInstance.post(
                `/api/user-management/v1/users/saveUserDetails`,
                {
                    mstPartnerId: +payload.custId,
                    mstUsrId: state.mappingDashboard.userEditingState.id,
                    loginId: state.mappingDashboard.userEditingState.email,
                    usrNm: state.mappingDashboard.userEditingState.name,
                    contactNo: state.mappingDashboard.userEditingState.phnNo,
                    title: state.mappingDashboard.userEditingState.title,
                    contactPerson: null,
                    mstGrp: state.mappingDashboard.userEditingState.group.filter(i=>i).map((g: any, i) => {
                      return {
                        mstGrpId: g,
                        usrGrpNm: state.mappingDashboard.userEditingState.userGrpName[i],
                        countryAccess: state.mappingDashboard.userEditingState.countryAccess,
                        locationAccess: state.mappingDashboard.userEditingState.locationAccess,
                      };
                    }),
                    // send numeric AccountStatus id as string (backend expects id strings)
                    // userEditingState.status is already a numeric value (0-6)
                    status: String(state.mappingDashboard.userEditingState.status),
                    // send password expiry flag to backend ('Y'|'N')
                    pwdExpired: state.mappingDashboard.userEditingState.pwdExpired,
                    countryShortCode: state.mappingDashboard.userEditingState.country,
                    isRootUser: state.mappingDashboard.userEditingState.isRootUser,
                    partnerNm: state.mappingDashboard.companyDetails.companyName,
                    countryAccess: state.mappingDashboard.userEditingState.countryAccess,
                    locationAccess: state.mappingDashboard.userEditingState.locationAccess,
                    appName: state.mappingDashboard.userEditingState.appName?.filter((i: any) => i) ?? [],
                    //secretKey: (password && password !== '')?password:null
                  }
            )
            const responseCode = String(response?.data?.responseCode ?? '').toUpperCase();
            const responseMsg = response?.data?.responseMsg || response?.data?.message || 'User details saved successfully.';

            if (responseCode !== 'SUCCESS') {
                toast.error(responseMsg)
                return
            }

            const isNewUser = state.mappingDashboard.userEditingState.id === null || state.mappingDashboard.userEditingState.id === undefined || state.mappingDashboard.userEditingState.id === '';
            toast.success(isNewUser ? 'User added. A verification link has been sent to their email.' : responseMsg)
        }catch(e:any){
            const errorMsg = e?.response?.data?.responseMsg || e?.response?.data?.message || e?.message || 'Please Try Again';
            toast.error(errorMsg)
        }

    }
)


export const saveEditCompanyDetails = createAsyncThunk('mappingDashboard/saveEditCompanyDetails',
    async (payload:any, {getState}:any)=>{
        const state:RootState = getState()
        try{
            console.log(state,'<<<state');
            const response = await axiosInstance.post(
                `/api/user-management/v1/customer/editCustomerDetails`,
            {
                mstPartnerId: payload.custId,
                mstUsrId: 1, // field doesn't do anything so its hardcoded to a random value since sending null gave 400
                companyName: state.mappingDashboard.companyDetails.companyName,
                phoneNumber: state.mappingDashboard.companyDetails.phoneNo,
                taxId: state.mappingDashboard.companyDetails.taxId,
                address: state.mappingDashboard.companyDetails.address,
                countryCode :state.mappingDashboard.companyDetails.country
            }
            )
            toast.success("Customer Details updated successfully!")
        }catch{
            toast.error("Failed to update Customer Details!")
        }

    }
)

export const preRegisterCustomersFromExcel = createAsyncThunk(
  'mappingDashboard/preRegisterCustomersFromExcel',
  async (payload: { filePath: string }) => {
    try {
      const response = await axiosInstance.post(
        `/api/user-management/v1/customer/preRegisterCustomersFromExcel`,
        payload
      );
      return response?.data;
    } catch (error) {
      console.error('Error: preRegisterCustomersFromExcel in MappingActions.ts', error);
      toast.error('Error uploading file');
      throw error;
    }
  }
);
